import { Container, Graphics, Sprite, Text } from "pixi.js";
import Scene from "./Scene";
import gsap from "gsap";
import { DEFAULT_JACKPOT_HIT } from "../StaticConfig";
import GlobalStore from "../services/GlobalStore";
import { Video } from "../services/Video";
import { Odometer } from "../components/odometer/Odometer";
import { audioManager } from "../services/AudioManager";
import { JackpotHitData } from "../models/JackpotHitData";

type SceneJackpotHitProps = {
  parent: Container;
  name: string;
};

const STAGE = {
  width: 1920,
  height: 1080,
};

class SceneJackpotHit extends Scene {
  private showTimeline: gsap.core.Timeline | undefined;
  private hideTimeline: gsap.core.Timeline | undefined;
  private currentTimeline: gsap.core.Timeline | null = null;

  private title: Sprite;
  private jpOdometer: Odometer;
  private jpInfo: Container;
  private jpInfoY: number = 0;
  private locationText: Text;
  private additionalsText: Text;
  private frame: Sprite;
  private shine: Video;
  private coinsStart: Video;
  private coinsLoop: Video;
  private shineLoopTimer: number = 0;
  private spinDuration: number = 0;
  private spinDelay: number = 0;
  private curJpData: JackpotHitData | null = null;

  constructor({ parent, name }: SceneJackpotHitProps) {
    super({
      parent: parent,
      name: name,
      // bgVideoPath: DEFAULT_JACKPOT_HIT.video_path,
      // bgImagePath: DEFAULT_JACKPOT_HIT.image_path,
    });

    // const shade = Sprite.from(`jackpot-hit-shade`);
    // this.contentContainer.addChild(shade);

    const ticker = GlobalStore.getApp()?.ticker;

    this.shine = Video.fromAlias("classic/video-animation/panel_shine", {
      ticker: ticker,
      loop: true,
      muted: true,
      autoplay: false,
    });

    this.coinsStart = Video.fromAlias("classic/video-animation/fall-start", {
      ticker: ticker,
      loop: false,
      muted: true,
      autoplay: false,
    });

    this.coinsLoop = Video.fromAlias("classic/video-animation/fall-loop", {
      ticker: ticker,
      loop: true,
      muted: true,
      autoplay: false,
    });

    this.coinsStart.onEnded(() => {
      this.coinsStart.alpha = 0;
      this.coinsLoop.alpha = 1;
      this.coinsLoop.play(0);
    });

    for (const vid of [this.coinsStart, this.coinsLoop]) {
      vid.alpha = 0;
      vid.width = STAGE.width;
      vid.height = STAGE.height;
      vid.x = STAGE.width / 2;
      vid.y = STAGE.height / 2;
      vid.anchor.set(0.5, 0.5);
      this.contentContainer.addChild(vid);
    }

    // const plonk = Sprite.from(`_plonk_hit`);
    // this.bgContainer.addChild(plonk);
    // plonk.zIndex = 3;

    const rays = Sprite.from(`jackpot-background-rays`);
    // rays.zIndex = 1;
    this.contentContainer.addChild(rays);

    this.title = Sprite.from(`ganador`);
    this.title.anchor.set(0.5, 0.5);
    this.title.position.set(STAGE.width / 2 - 5, 222);

    const frame = Sprite.from(`jackpot-frame`);
    frame.anchor.set(0.5, 0.5);
    frame.position.set(STAGE.width / 2, STAGE.height / 2 + 27);
    this.frame = frame;

    this.frame.addChild(this.shine);
    this.shine.anchor.set(0.5, 0.5);
    this.shine.position.set(0, -25);
    this.shine.scale.set(2);

    const fontFamily: string = "Avenir Next Condensed Demi Bold";
    const jpDef = DEFAULT_JACKPOT_HIT;

    this.spinDuration = jpDef.spin_duration;
    this.spinDelay = jpDef.delay_show;

    this.jpInfo = new Container();
    const jpiBg = Sprite.from("frame-tab");
    this.jpInfo.addChild(jpiBg);
    jpiBg.anchor.set(0.5, 0.5);
    this.jpInfo.x = frame.x;
    this.jpInfo.y = frame.y + 277 - this.jpInfo.height / 2;
    this.jpInfoY = frame.y + 277;

    const jpInfoMask = new Graphics();
    jpInfoMask
      .rect(0, frame.y + 150, 1920, 300)
      .fill({ color: "0xff0000", alpha: 1 });
    this.jpInfo.mask = jpInfoMask;

    let hasAdditional = false;
    {
      const config = GlobalStore.generalSettings;
      hasAdditional =
        config.showMachineIdOnHitScreen ||
        config.showDateOnHitScreen ||
        config.showTimeOnHitScreen;
    }

    const locText = new Text({
      text: "SANTO DOMINGO",
      style: {
        fontFamily: fontFamily,
        fontSize: hasAdditional ? 80 : 100,
        fill: "0xffffff",
        align: "center",
      },
    });
    locText.x = 0;
    locText.y = hasAdditional ? -25 : 0;
    locText.anchor.set(0.5, 0.5);
    this.jpInfo.addChild(locText);
    this.locationText = locText;

    const addText = new Text({
      text: "asdf",
      style: {
        fontFamily: fontFamily,
        fontSize: 40,
        fill: "0xffffff",
        align: "center",
      },
    });
    addText.x = 0;
    addText.y = 58;
    addText.anchor.set(0.5, 0.5);
    this.jpInfo.addChild(addText);
    this.additionalsText = addText;

    for (const c of [this.title, this.jpInfo, jpInfoMask, frame]) {
      this.contentContainer.addChild(c);
    }

    this.jpOdometer = new Odometer({
      digitCount: 10,
      fontFamily: "Faustina",
      fontSize: jpDef.size,
      formatter: (value: number) => GlobalStore.formatCurrency(value),
      // spacing: -10,
      maxWidth: 1500,
    });
    this.jpOdometer.contentAnchor = 0.5;
    this.jpOdometer.x = jpDef.x;
    this.jpOdometer.y = jpDef.y;
    this.contentContainer.addChild(this.jpOdometer);
  }

  show(): void {
    super.show();

    console.log("Showing jackpot win");

    const jackpotData = GlobalStore.getJackpotHitData();
    if (jackpotData === null) {
      throw new Error("Invalid jackpot data");
    }

    // Trigger jackpot hit audio sequence
    audioManager.onJackpotHit(jackpotData);

    this.curJpData = jackpotData;

    if (this.hideTimeline) {
      this.hideTimeline.pause(0);
    }

    if (this.showTimeline) {
      this.showTimeline.kill();
    }

    this.showTimeline = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        if (this.curJpData === null) {
          throw new Error("No jackpot hit selected.");
        }
        this.title.alpha = 0;
        this.coinsStart.alpha = 0;
        this.contentContainer.alpha = 0;
        this.jpOdometer.alpha = 0;
        // Set location text with machine ID if enabled
        this.locationText.text = this.curJpData.location;
        this.additionalsText.text = "";

        this.jpInfo.y = this.jpInfoY - this.jpInfo.height / 2;

        const locale = GlobalStore.getDateLocale();
        if (GlobalStore.generalSettings.showDateOnHitScreen) {
          this.additionalsText.text +=
            this.curJpData.timestamp.toLocaleDateString(locale);
        }
        if (GlobalStore.generalSettings.showTimeOnHitScreen) {
          if (this.additionalsText.text.length > 0) {
            this.additionalsText.text += "   |   ";
          }
          this.additionalsText.text +=
            this.curJpData.timestamp.toLocaleTimeString(locale, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            });
        }
        if (GlobalStore.generalSettings.showMachineIdOnHitScreen) {
          if (this.additionalsText.text.length > 0) {
            this.additionalsText.text += "   |   ";
          }
          this.additionalsText.text += `ID: ${this.curJpData.machineId}`;
        }
      },
    });

    this.showTimeline
      .addLabel("start")
      .fromTo(
        this.contentContainer,
        {
          pixi: {
            alpha: 0,
          },
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.5,
          ease: "sine.out",
        },
      )
      .fromTo(
        this.coinsStart,
        {
          pixi: {
            alpha: 0,
          },
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.2,
          ease: "sine.out",
          onStart: () => {
            this.coinsStart.play(0);
          },
        },
      )
      .call(() => {
        this.shine.play(0);
      })
      .fromTo(
        this.frame,
        { alpha: 0 },
        { alpha: 1, ease: "sine.out", duration: 0.5 },
        "start+=0.75",
      )
      .fromTo(
        this.jpOdometer,
        {
          alpha: 0,
        },
        {
          alpha: 1,
          onStart: (value: number, spinDuration: number) => {
            this.jpOdometer.rotateTo(0, { duration: 0 });
            this.jpOdometer.rotateTo(value, {
              duration: spinDuration,
              easing: "sine.out",
              animate: false,
            });
          },
          onStartParams: [this.curJpData.value, this.spinDuration],
          delay: this.spinDelay,
          ease: "sine.out",
        },
      )
      .addLabel("odometerStart")
      .fromTo(
        this.title,
        {
          pixi: {
            alpha: 0,
          },
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.75,
          ease: "sine.out",
        },
        `odometerStart+=${1}`,
      )
      .fromTo(
        this.jpInfo,
        {
          y: this.jpInfoY - this.jpInfo.height / 2,
        },
        {
          y: this.jpInfoY,
          duration: 0.75,
          ease: "sine.out",
        },
        `odometerStart+=${this.spinDuration}`,
      );

    this.showTimeline.play(0);
  }

  public hide(): void {
    gsap.to(this, {
      pixi: {
        alpha: 0,
      },
      duration: 0.5,
      ease: "sine.in",
      onComplete: () => {
        this.hideInstant();
      },
      onInterrupt: () => {
        this.hideInstant();
      },
    });
    return;
    // if (!this.hideTimeline) {
    //   this.hideTimeline = gsap.timeline();

    //   this.hideTimeline.addLabel("start").to(this.jpText, {
    //     alpha: 0,
    //     onComplete: (counter: TextCounterController) => {
    //       counter.stop();
    //       super.hide();
    //     },
    //     onCompleteParams: [this.jpController],
    //   });
    // }

    // this.hideTimeline.play(0);
  }

  public fadeOut = (force?: boolean): gsap.core.Animation | null => {
    if (!this.visible && !force) return null;

    const tl = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        if (this.currentTimeline) {
          this.currentTimeline.kill();
        }
        this.currentTimeline = tl;
      },
      onComplete: () => {
        if (this.currentTimeline === tl) {
          this.currentTimeline.kill();
          this.currentTimeline = null;
        }
        this.hideInstant();
      },
    });

    tl.addLabel("start").to(this.contentContainer, {
      pixi: {
        alpha: 0,
      },
      duration: 0.5,
      ease: "sine.in",
    });

    return tl;
  };

  public hideInstant = (): void => {
    this.showTimeline?.pause(0);
    this.hideTimeline?.pause(0);

    this.coinsLoop.stop();
    this.coinsLoop.alpha = 0;
    clearTimeout(this.shineLoopTimer);
    this.shineLoopTimer = 0;
    this.coinsStart.stop();
    this.coinsStart.alpha = 0;

    audioManager.stopAll();

    super.hide();
  };
}

export default SceneJackpotHit;
